import React, { FC, useState } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Typography from "../typography/Typography";
import { useBackgroundColor } from "../../utils";
import { ChevronDown } from "../icon";
import Html from "../html/Html";
import { useDataLayerPush } from "../../hooks/useDataLayerPush";

// Interface

export interface TableProps {
  rows?: Row[];
  firstColumnHeading?: string;
}

interface Row {
  heading: string;
  cells: Cell[];
}

interface RowProps extends Row {
  numberOfCells: number;
}

interface Cell {
  heading: string;
  content: string; // Rich text editor
}

interface StyledRowHeaderProps {
  isOpen: boolean;
  width: number;
}

interface StyledCells {
  isOpen: boolean;
  numberOfCells: number;
}

// Styles

const StyledTable = styled.div`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    border: 1px solid currentColor;
    border-radius: ${borderRadii.small};
    overflow: hidden;
  `};
`;

const StyledTableHeader = styled.div`
  ${({ theme: { maxMedia } }) => css`
    border-bottom: 1px solid currentColor;

    @media (max-width: ${maxMedia.medium}) {
      display: none;
    }
  `};
`;

const StyledRow = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    &:not(:last-child) {
      border-bottom: 1px solid currentColor;
    }

    @media (min-width: ${media.medium}) {
      display: flex;
    }
  `};
`;

const StyledRowHeader = styled.div<StyledRowHeaderProps>`
  ${({ theme: { space, colors, media }, isOpen, width }) => css`
    padding: ${space.small};
    ${useBackgroundColor("neutral")};

    button {
      display: flex;
      justify-content: space-between;
      width: 100%;
      cursor: pointer;
      text-align: left;

      svg {
        transition: transform 0.2s ease;
        transform: rotate(${isOpen ? "180deg" : "0deg"});
      }
    }

    @media (min-width: ${media.medium}) {
      width: ${width}%;
      border-right: 1px solid currentColor;

      button {
        cursor: default;
        svg {
          display: none;
        }
      }
    }
  `};
`;

const StyledCell = styled.div`
  ${({ theme: { space, colors, media, maxMedia } }) => css`
    padding: ${space.small};
    ${useBackgroundColor("white")};

    @media (max-width: ${maxMedia.medium}) {
      &:not(:last-child) {
        border-bottom: 1px solid currentColor;
      }
    }

    @media (min-width: ${media.medium}) {
      border-right: 1px solid currentColor;

      &:last-child {
        border-right: none;
      }
    }
  `};
`;

const StyledCellHeading = styled(Typography)`
  ${({ theme: { space, colors, media } }) => css`
    margin-bottom: ${space.xSmall};

    @media (min-width: ${media.medium}) {
      display: none;
    }
  `};
`;

const StyledCells = styled.div<StyledCells>`
  ${({
    theme: { space, colors, media, maxMedia },
    isOpen,
    numberOfCells,
  }) => css`
    @media (max-width: ${maxMedia.medium}) {
      display: ${isOpen ? "block" : "none"};
      border-top: 1px solid currentColor;
    }

    @media (min-width: ${media.medium}) {
      display: grid;
      width: 100%;
      grid-template-columns: repeat(${numberOfCells}, 1fr);
    }
  `};
`;

const StyledColumnHeading = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    padding: ${space.small};
    ${useBackgroundColor("neutral")};
    border-right: 1px solid currentColor;

    &:last-child {
      border-right: none;
    }
  `};
`;

// JSX

const Row: FC<RowProps> = ({ heading, cells, numberOfCells }) => {
  const { dataLayerPush } = useDataLayerPush();
  const [isOpen, setIsOpen] = useState(false);

  const handleOnlick = () => {
    setIsOpen(!isOpen);

    dataLayerPush({
      event: "table_column_toggle",
      tableRow: heading,
      tableRowState: !isOpen ? "open" : "closed",
    });
  };

  return (
    <StyledRow>
      <StyledRowHeader isOpen={isOpen} width={100 / (numberOfCells + 1)}>
        <button type="button" onClick={handleOnlick}>
          <Typography component="span" variant="bodyBold">
            {heading}
          </Typography>
          <ChevronDown />
        </button>
      </StyledRowHeader>

      <StyledCells isOpen={isOpen} numberOfCells={numberOfCells}>
        {cells.map((cell, index) => (
          <StyledCell key={index}>
            {cell.heading && (
              <StyledCellHeading component="h4" variant="bodyBold">
                {cell.heading}
              </StyledCellHeading>
            )}
            <Html>{cell.content}</Html>
          </StyledCell>
        ))}
      </StyledCells>
    </StyledRow>
  );
};

export const Table: FC<TableProps> = ({ rows, firstColumnHeading }) => {
  const maxCells =
    rows?.reduce(
      (acc, row) => (row.cells.length > acc ? row.cells.length : acc),
      0
    ) || 1;

  const hasHeadings = rows?.some((row) =>
    row.cells.some((cell) => cell.heading)
  );

  const largestRow = rows?.reduce(
    (acc, row) => (row.cells.length > acc.cells.length ? row : acc),
    { heading: "", cells: [] as Cell[] }
  );

  const cellHeadings = largestRow?.cells.map((cell) => cell.heading);

  return (
    <StyledTable>
      {hasHeadings && (
        <StyledTableHeader>
          <StyledRow>
            <StyledRowHeader isOpen width={100 / (maxCells + 1)}>
              {firstColumnHeading && (
                <Typography component="span" variant="bodyBold">
                  {firstColumnHeading}
                </Typography>
              )}
            </StyledRowHeader>

            <StyledCells isOpen numberOfCells={maxCells}>
              {cellHeadings?.map((heading, index) => (
                <StyledColumnHeading key={index}>
                  <Typography component="h4" variant="bodyBold">
                    {heading}
                  </Typography>
                </StyledColumnHeading>
              ))}
            </StyledCells>
          </StyledRow>
        </StyledTableHeader>
      )}

      {rows?.map((row, index) => (
        <Row key={index} {...row} numberOfCells={maxCells} />
      ))}
    </StyledTable>
  );
};

export default Table;
